import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { vendorsApi, branchesApi } from '@/lib/apiServices';
import { useApiList } from '@/lib/hooks/useApiList';
import '../marketplace.css';
import { getVendorSubdomainUrl } from '@/lib/subdomain';

const TABS = [
  { key: 'branches', label: 'شعب رسمی', icon: 'fa-building' },
  { key: 'direct', label: 'عرضه مستقیم', icon: 'fa-industry' },
  { key: 'artisan', label: 'غرفه‌داران', icon: 'fa-store' },
];

const PRODUCER_TYPES = [
  { value: 'all', label: 'همه' },
  { value: 'farm', label: 'مزرعه' },
  { value: 'factory', label: 'کارخانه' },
  { value: 'nomad_village', label: 'روستای عشایری' },
];

const PINNED_VENDOR_SLUG = 'savin';

function withPinnedFirst(data) {
  if (!data) return [];
  return [...data].sort(
    (a, b) => Number(b.slug === PINNED_VENDOR_SLUG) - Number(a.slug === PINNED_VENDOR_SLUG),
  );
}

function ShopCard({ to, name, banner, locationLabel, address, featured }) {
  const isExternal = /^https?:\/\//.test(to);
  const CardTag = isExternal ? 'a' : Link;
  const linkProp = isExternal ? { href: to } : { to };
  const title = name || 'شعبه رسمی ایران چارسوق';
  const initial = title.trim()[0];

  return (
    <CardTag {...linkProp} className="shop-card">
      <div className="shop-card-banner">
        {banner ? (
          <>
            <img className="shop-card-banner-backdrop" src={banner} alt="" aria-hidden="true" />
            <img className="shop-card-banner-image" src={banner} alt="" loading="lazy" />
          </>
        ) : (
          <span className="shop-card-banner-placeholder">{initial}</span>
        )}
        <span className="shop-card-banner-shade" />
        {featured && <span className="shop-card-featured">ویژه</span>}
        <span className="shop-card-avatar" aria-hidden="true">{initial}</span>
      </div>

      <div className="shop-card-body">
        <h2 title={title}>{title}</h2>
        {locationLabel && (
          <p className="shop-card-location">
            <i className="fas fa-map-marker-alt" aria-hidden="true" /> {locationLabel}
          </p>
        )}
        {address && <p className="shop-card-address" title={address}>{address}</p>}

        <span className="shop-card-cta">
          مشاهده فروشگاه <i className="fas fa-arrow-left" aria-hidden="true" />
        </span>
      </div>
    </CardTag>
  );
}

function ShopCardSkeleton() {
  return (
    <div className="shop-card-skeleton" aria-hidden="true">
      <div className="shop-card-skeleton-banner skeleton" />
      <div className="shop-card-skeleton-content">
        <span className="skeleton" />
        <span className="skeleton" />
        <span className="skeleton" />
      </div>
    </div>
  );
}

function EmptyState({ onClear, hasFilters }) {
  return (
    <div className="marketplace-state marketplace-empty">
      <span className="marketplace-state-icon" aria-hidden="true">
        <i className="fas fa-store-slash" />
      </span>
      <h2>موردی پیدا نشد</h2>
      <p>با تغییر عبارت جست‌وجو یا فیلترها دوباره تلاش کنید.</p>
      {hasFilters && (
        <button type="button" className="marketplace-clear-button" onClick={onClear}>
          پاک کردن فیلترها
        </button>
      )}
    </div>
  );
}

export default function Marketplace() {
  const [activeTab, setActiveTab] = useState('branches');
  const [producerType, setProducerType] = useState('all');
  const [province, setProvince] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const fetchBranches = useCallback(() => branchesApi.list(), []);
  const fetchDirect = useCallback(
    () => vendorsApi.list({
      is_direct_to_consumer: true,
      ...(producerType === 'all' ? {} : { producer_type: producerType }),
    }),
    [producerType],
  );
  const fetchArtisan = useCallback(() => vendorsApi.list({ is_direct_to_consumer: false }), []);

  const branches = useApiList(fetchBranches, [fetchBranches]);
  const direct = useApiList(fetchDirect, [fetchDirect]);
  const artisan = useApiList(fetchArtisan, [fetchArtisan]);
  const listByTab = { branches, direct, artisan };
  const currentData = listByTab[activeTab].data;
  const currentLoading = listByTab[activeTab].loading;
  const currentError = listByTab[activeTab].error;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim().toLocaleLowerCase()), 220);
    return () => window.clearTimeout(timer);
  }, [search]);

  const provinceOptions = useMemo(() => (
    [...new Set(
      branches.data
        .map((branch) => branch.location?.province_name_fa)
        .filter(Boolean),
    )].sort((a, b) => a.localeCompare(b, 'fa'))
  ), [branches.data]);

  const sortedData = activeTab === 'branches' ? currentData : withPinnedFirst(currentData);
  const filteredData = sortedData.filter((item) => {
    if (activeTab === 'branches' && province !== 'all'
      && item.location?.province_name_fa !== province) return false;
    if (!debouncedSearch) return true;
    const searchable = activeTab === 'branches'
      ? `${item.vendor_shop_name_fa ?? ''} ${item.vendor_shop_name_en ?? ''} ${item.address ?? ''} ${item.location?.name_fa ?? ''} ${item.location?.province_name_fa ?? ''}`
      : `${item.vendor_shop_name_fa ?? ''} ${item.vendor_shop_name_en ?? ''}`;
    return searchable.toLocaleLowerCase().includes(debouncedSearch);
  });

  const hasFilters = Boolean(search.trim())
    || (activeTab === 'branches' && province !== 'all')
    || (activeTab === 'direct' && producerType !== 'all');

  const clearFilters = () => {
    setSearch('');
    setProvince('all');
    setProducerType('all');
  };

  const branchLocation = (branch) => {
    const city = branch.location?.name_fa;
    const provinceName = branch.location?.province_name_fa;
    return [city, provinceName]
      .filter((value, index, list) => value && list.indexOf(value) === index)
      .join('، ');
  };

  return (
    <main className="marketplace-page">
      <div className="container">
        <header className="marketplace-hero">
          <span className="marketplace-hero-mark" aria-hidden="true">
            <i className="fas fa-store" />
          </span>
          <p className="marketplace-eyebrow">ایران چارسوق</p>
          <h1>فروشندگان و شعب ایران چارسوق</h1>
          <p className="marketplace-subtitle">محصولات و فروشگاه‌های معتبر را از نزدیک پیدا کنید.</p>
        </header>

        <nav className="marketplace-tabs" data-active-tab={activeTab} aria-label="دسته‌بندی فروشگاه‌ها">
          <span className="marketplace-tab-indicator" aria-hidden="true" />
          {TABS.map((tab) => {
            const tabList = listByTab[tab.key];
            const loaded = !tabList.loading && !tabList.error;
            return (
              <button
                key={tab.key}
                type="button"
                className={`marketplace-tab${activeTab === tab.key ? ' active' : ''}`}
                aria-pressed={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={`fas ${tab.icon}`} aria-hidden="true" />
                <span>{tab.label}</span>
                {loaded && <span className="marketplace-tab-count">{tabList.data.length.toLocaleString('fa-IR')}</span>}
              </button>
            );
          })}
        </nav>

        <section className="marketplace-toolbar" aria-label="جست‌وجو و فیلتر فروشگاه‌ها">
          <label className="marketplace-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="جست‌وجوی نام فروشگاه..."
              aria-label="جست‌وجوی نام فروشگاه"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label="پاک کردن جست‌وجو">
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            )}
          </label>

          {activeTab === 'branches' && (
            <label className="marketplace-select-wrap">
              <i className="fas fa-map-marker-alt" aria-hidden="true" />
              <select aria-label="فیلتر بر اساس استان" value={province} onChange={(event) => setProvince(event.target.value)}>
                <option value="all">همهٔ استان‌ها</option>
                {provinceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <i className="fas fa-chevron-down marketplace-select-chevron" aria-hidden="true" />
            </label>
          )}

          {activeTab === 'direct' && (
            <div className="marketplace-producer-filter" role="group" aria-label="نوع تولیدکننده">
              {PRODUCER_TYPES.map((type) => (
                <button
                  type="button"
                  key={type.value}
                  className={producerType === type.value ? 'active' : ''}
                  aria-pressed={producerType === type.value}
                  onClick={() => setProducerType(type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="marketplace-results-row">
          <p aria-live="polite">
            <strong>{currentLoading ? '…' : filteredData.length.toLocaleString('fa-IR')}</strong> مورد یافت شد
          </p>
          {hasFilters && (
            <button type="button" className="marketplace-clear-button" onClick={clearFilters}>
              پاک کردن فیلترها
            </button>
          )}
        </div>

        {currentLoading ? (
          <div className="marketplace-grid" aria-label="در حال بارگذاری فروشگاه‌ها">
            {Array.from({ length: 6 }, (_, index) => <ShopCardSkeleton key={index} />)}
          </div>
        ) : currentError ? (
          <div className="marketplace-state" role="alert">
            <span className="marketplace-state-icon" aria-hidden="true">
              <i className="fas fa-triangle-exclamation" />
            </span>
            <h2>بارگذاری فروشگاه‌ها انجام نشد</h2>
            <p>لطفاً اتصال خود را بررسی و دوباره تلاش کنید.</p>
            <button type="button" className="marketplace-retry-button" onClick={listByTab[activeTab].refetch}>
              <i className="fas fa-rotate-right" aria-hidden="true" /> تلاش دوباره
            </button>
          </div>
        ) : filteredData.length === 0 ? (
          <EmptyState onClear={clearFilters} hasFilters={hasFilters} />
        ) : (
          <div className="marketplace-grid">
            {activeTab === 'branches'
              ? filteredData.map((branch) => (
                  <ShopCard
                    key={branch.id}
                    to={`/branches/${branch.id}`}
                    name={branch.vendor_shop_name_fa}
                    banner={branch.vendor_banner_url}
                    address={branch.address}
                    locationLabel={branchLocation(branch)}
                  />
                ))
              : filteredData.map((vendor) => (
                  <ShopCard
                    key={vendor.id}
                    to={getVendorSubdomainUrl(vendor.slug)}
                    name={vendor.vendor_shop_name_fa}
                    banner={vendor.vendor_banner_url}
                    featured={vendor.slug === PINNED_VENDOR_SLUG}
                  />
                ))}
          </div>
        )}
      </div>
    </main>
  );
}
